/**
 * Harness for showing a *real* product screen on a public marketing page.
 *
 * The public site used to draw imitations of the product in bespoke Tailwind,
 * which meant the marketing screens drifted from the app the moment anyone
 * shipped a change. Instead, these demos mount the same components the
 * authenticated app renders and feed them fixture data.
 *
 * Two things make that possible for components that fetch:
 *
 *  - a private QueryClient whose cache is pre-seeded with the exact query keys
 *    the screen asks for, so the real success state renders without a request;
 *  - `inert`, because none of it is wired to a backend. A marketing visitor
 *    must not be able to click "Save note" or tab into a form that cannot
 *    submit. This is a picture of the product, built from the product.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  QueryClient,
  QueryClientProvider,
  type DefaultError,
  type DefaultedQueryObserverOptions,
  type QueryKey,
  type QueryObserverOptions,
} from '@tanstack/react-query';
import { TooltipProvider } from '@/components/ui/tooltip';

/** One pre-seeded cache entry: the query key a screen reads, and its data. */
export interface DemoSeedEntry {
  queryKey: readonly unknown[];
  data: unknown;
}

/**
 * How much of a screen is shown.
 *
 * `lead` is the headline screen on a page and gets the taller viewport;
 * `preview` is a supporting screen further down. Both bound the screen and let
 * the visitor scroll it. `full` lets the screen take its natural height, for a
 * page whose whole point is one complete screen.
 */
export type DemoCrop = 'lead' | 'preview' | 'full';

/** How tall the scroll viewport is on a desktop, per crop. */
const VIEWPORT_HEIGHT: Record<'lead' | 'preview', string> = {
  lead: 'max-h-[48rem]',
  preview: 'max-h-[34rem]',
};

/** Where the layout has the room for a scroll viewport (Tailwind's `lg`). */
const SCROLLABLE_QUERY = '(min-width: 1024px)';

/**
 * Whether this viewport may scroll at all.
 *
 * Driven by `matchMedia` rather than a `lg:` class because it decides more than
 * styling: on a phone the screen is clipped with an expand control instead, and
 * a region that cannot scroll must not be focusable or announce itself as
 * scrollable. Defaults to the clipped form, which is the safe one — it never
 * promises an interaction that is not there.
 */
function useCanScroll(): boolean {
  const [canScroll, setCanScroll] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(SCROLLABLE_QUERY);
    const update = () => setCanScroll(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);

  return canScroll;
}

/**
 * How much is shown on a phone before the "show the whole screen" toggle. A
 * nested scroll area would swallow a swipe meant for the page, so small screens
 * clip and expand instead of scrolling.
 */
const MOBILE_CLIP: Record<'lead' | 'preview', string> = {
  lead: 'max-h-[32rem]',
  preview: 'max-h-[26rem]',
};

/**
 * The space a screen takes before it has arrived.
 *
 * A demo is a lazily loaded chunk mounted after the page has painted (and, on
 * a prerendered page, after React takes over), so the frame sits empty first.
 * Reserving the height the screen will actually occupy — the mobile clip, or
 * the desktop viewport it scrolls inside — keeps the arrival from shoving
 * everything below it down the page.
 */
export const DEMO_RESERVED_HEIGHT: Record<DemoCrop, string> = {
  lead: 'min-h-[32rem] lg:min-h-[48rem]',
  preview: 'min-h-[26rem] lg:min-h-[34rem]',
  // An uncropped screen has no bound to reserve; hold a screenful and let the
  // rest arrive.
  full: 'min-h-[32rem]',
};

/** Treat anything within this many pixels of the bottom as "at the end". */
const END_SLACK = 24;

interface DemoViewportProps {
  crop: DemoCrop;
  /** Override the desktop viewport height, e.g. `max-h-[40rem]`. */
  height?: string;
  /** What the scroll region is called, for keyboard and screen-reader users. */
  label?: string;
  /** Changing this scrolls back to the top — a new section starts at its start. */
  resetKey?: string;
  children: ReactNode;
}

/**
 * The window a marketing visitor looks at the product through.
 *
 * The screens used to be *clipped*: whatever did not fit was simply gone, which
 * made the product look like a sneak preview of itself. Here the frame stays a
 * sensible size on the page and the product scrolls inside it, the way the real
 * modal scrolls for a recruiter — nothing is cut off, it is just below the fold
 * of the screen.
 *
 * The scroll container must stay *outside* `InertRegion`. An inert,
 * pointer-events-none subtree is not a hit target, so a wheel or touch gesture
 * over it would scroll the page instead of the screen.
 */
export function DemoViewport({ crop, height, label, resetKey, children }: DemoViewportProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const canScroll = useCanScroll();
  const [expanded, setExpanded] = useState(false);
  const [moreBelow, setMoreBelow] = useState(false);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    const update = () =>
      setMoreBelow(node.scrollHeight - node.clientHeight - node.scrollTop > END_SLACK);
    update();
    node.addEventListener('scroll', update, { passive: true });
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => update());
    observer?.observe(node);
    return () => {
      node.removeEventListener('scroll', update);
      observer?.disconnect();
    };
  }, [canScroll, expanded, resetKey]);

  /** A new section starts at its beginning, not wherever the last one was left. */
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [resetKey]);

  if (crop === 'full') return <>{children}</>;

  /*
    No `overscroll-contain` on the scrolling form: a visitor scrolling past the
    demo must carry on down the page once the screen reaches its end, rather
    than being held inside a box in the middle of a marketing page.
  */
  const bounds = canScroll
    ? `${height ?? VIEWPORT_HEIGHT[crop]} overflow-y-auto`
    : expanded
      ? ''
      : `${MOBILE_CLIP[crop]} overflow-hidden`;

  return (
    <div data-testid="demo-viewport" data-mode={canScroll ? 'scroll' : 'expand'}>
      <div className="relative">
        <div
          ref={scrollRef}
          // Only a region that really scrolls may be focused and announced as one.
          tabIndex={canScroll ? 0 : undefined}
          role={canScroll ? 'group' : undefined}
          aria-label={
            canScroll
              ? `${label ?? 'Product preview'}: scroll to see the rest of the screen`
              : undefined
          }
          className={`focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${bounds}`}
          data-testid="demo-scroll-region"
        >
          {children}
        </div>
        {moreBelow ? (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 flex h-20 items-end justify-center bg-gradient-to-t from-background via-background/80 to-transparent"
          >
            {canScroll ? (
              <span className="mb-2 rounded-full bg-background/90 px-2.5 py-1 text-[11px] font-medium text-base-text-muted shadow-sm ring-1 ring-border">
                Scroll for more
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
      {/*
        A phone gets a control instead of a scroll area: a nested scroll region
        swallows the swipe meant for the page.
      */}
      {canScroll ? null : (
        <div className="flex justify-center border-t bg-muted/30">
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="px-3 py-2 text-xs font-medium text-primary underline-offset-2 hover:underline"
            data-testid="button-demo-expand"
          >
            {expanded ? 'Show less' : 'Show the whole screen'}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * A QueryClient that cannot fetch.
 *
 * `defaultOptions` is not enough: a product component that passes its own
 * `queryFn` and `staleTime: 0` (the recap and scheduling screens both mount
 * components that do) overrides the defaults and would go to the network the
 * moment it mounts — from a public page, as an anonymous visitor, for an
 * endpoint that answers 401. Forcing `enabled: false` *after* the component's
 * own options are merged is the only place that can be ruled out for every
 * screen at once. Seeded queries still render their data straight from the
 * cache; an unseeded one simply stays pending.
 */
class DemoQueryClient extends QueryClient {
  override defaultQueryOptions<
    TQueryFnData = unknown,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
    TPageParam = never,
  >(
    options:
      | QueryObserverOptions<TQueryFnData, TError, TData, TQueryData, TQueryKey, TPageParam>
      | DefaultedQueryObserverOptions<TQueryFnData, TError, TData, TQueryData, TQueryKey>,
  ): DefaultedQueryObserverOptions<TQueryFnData, TError, TData, TQueryData, TQueryKey> {
    return {
      ...super.defaultQueryOptions(options),
      enabled: false,
      staleTime: Infinity,
      gcTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
      retry: false,
    };
  }
}

function createDemoQueryClient(seed: readonly DemoSeedEntry[]): QueryClient {
  const client = new DemoQueryClient({
    defaultOptions: {
      queries: {
        /**
         * Only reachable through an imperative `refetch()`, since every query
         * is disabled above. Still never allowed to touch the API.
         */
        queryFn: () => {
          if (import.meta.env.DEV) {
            throw new Error(
              'Product demo: a query was not seeded. Add its key to the screen’s seed list.',
            );
          }
          return new Promise<never>(() => {});
        },
        retry: false,
        staleTime: Infinity,
        gcTime: Infinity,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      mutations: { retry: false },
    },
  });

  for (const entry of seed) {
    client.setQueryData(entry.queryKey, entry.data);
  }
  return client;
}

/**
 * Marks a subtree as look-but-don't-touch: out of the tab order, out of the
 * accessibility tree, and unclickable. Applied to a whole screen by default;
 * screens that expose one live control (the candidate panel's section rail)
 * wrap their remaining parts in this instead.
 */
export function InertRegion({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={className}
      // React 18 only forwards the lowercase string form of the attribute;
      // `pointer-events-none` covers browsers without `inert` support.
      {...({ inert: '' } as Record<string, string>)}
    >
      <div className="pointer-events-none select-none">{children}</div>
    </div>
  );
}

interface DemoScreenProps {
  /** Cache entries the mounted product components will read. */
  seed?: readonly DemoSeedEntry[];
  className?: string;
  /**
   * Opt out of blanket inertness because the screen exposes a live control of
   * its own. The screen then has to wrap everything else in `InertRegion` — a
   * visitor must never reach a control that cannot work.
   */
  interactive?: boolean;
  /**
   * Bound the screen in a scroll viewport. Omit it when the screen places its
   * own `DemoViewport` — the candidate panel scrolls its destination column
   * while its header and rail stay put, the way the real modal does.
   */
  crop?: DemoCrop;
  /** Override the viewport's desktop height for a screen that needs more room. */
  viewportHeight?: string;
  /** What the scroll region is called, for keyboard and screen-reader users. */
  label?: string;
  children: ReactNode;
}

export function DemoScreen({
  seed = [],
  className,
  interactive = false,
  crop,
  viewportHeight,
  label,
  children,
}: DemoScreenProps) {
  const [queryClient] = useState(() => createDemoQueryClient(seed));

  /**
   * The default `queryFn` above only catches components that rely on it. A
   * component that brings its own fetcher would go to the network if its seed
   * were missing or its key changed, so cancel any fetch that starts: the
   * screen keeps rendering its seeded state, and in development the mistake is
   * reported instead of silently costing a request.
   */
  useEffect(() => {
    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'fetch') return;
      void event.query.cancel({ silent: true });
      const key = JSON.stringify(event.query.queryKey);
      const message = `Product demo: ${key} tried to fetch. Add it to the screen's seed list.`;
      if (import.meta.env.DEV) {
        console.error(message);
      }
    });
  }, [queryClient]);

  const body = interactive ? (
    <div className={className}>{children}</div>
  ) : (
    <InertRegion className={className}>{children}</InertRegion>
  );

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        {crop ? (
          <DemoViewport crop={crop} height={viewportHeight} label={label}>
            {body}
          </DemoViewport>
        ) : (
          body
        )}
      </TooltipProvider>
    </QueryClientProvider>
  );
}
